import React, { useMemo } from "react";
import {
  DndContext,
  DragOverlay,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import Typography from "../../../common/Typography";
import Button from "../../../common/Button";
import { DragAndDropSorterContent } from "../../../../types/newtypes";
import { useDragAndDropSorterHook } from "./hooks/useDragAndDropSorterHook";
import { GripVertical } from "lucide-react";
import {
  DraggableItem,
  CategoryDropZone,
  FeedbackMessage,
} from "../ui-helpers";
import { useLessonDndSensors } from "./hooks/useLessonDndSensors";
import { useLessonDndAutoScroll } from "./hooks/useLessonDndAutoScroll";

interface DragAndDropSorterPreviewProps {
  content: DragAndDropSorterContent;
  onCompleted?: () => void;
  // eslint-disable-next-line no-unused-vars
  onCardStateChange?: (...args: [boolean, boolean]) => void;
  cardId: string;
}

const DragAndDropSorterPreview: React.FC<DragAndDropSorterPreviewProps> = ({
  content,
  onCardStateChange,
  cardId,
}) => {
  const {
    activeId,
    isCorrect,
    showFeedback,
    feedbackMessage,
    handleDragStart,
    handleDragEnd,
    handleTryAgain,
    handleMoveToAvailable,
    getDraggableItems,
    getCategories,
    getItemCorrectness,
  } = useDragAndDropSorterHook({
    content,
    onCardStateChange,
    cardId,
  });

  const sensors = useLessonDndSensors();
  const { startLessonDndAutoScroll, stopLessonDndAutoScroll } = useLessonDndAutoScroll();

  // Create a map of item IDs to their text for easy lookup
  const itemTexts = useMemo(() => {
    const texts: { [itemId: string]: string } = {};
    content?.items?.forEach((item) => {
      texts[item?.id] = item?.text;
    });
    return texts;
  }, [content?.items]);

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-4">
        <Typography variant="h4" weight="bold">
          {content?.slide_title}
        </Typography>

        <Typography variant="p" className="!text-base mb-4">
          {content?.intro_text}
        </Typography>

        <Typography variant="small">{content?.prompt}</Typography>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        autoScroll={false}
        onDragStart={(event) => { startLessonDndAutoScroll(); handleDragStart(event); }}
        onDragEnd={(event) => { stopLessonDndAutoScroll(); handleDragEnd(event); }}
        onDragCancel={stopLessonDndAutoScroll}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Left Column - First Category */}
          <div className="lg:col-span-1">
            <div className="bg-white lg:p-6 p-4 rounded-2xl border border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <Typography
                  variant="h6"
                  weight="bold"
                  className="!text-blue-500"
                >
                  🎯 {getCategories()[0]?.title || "Category 1"}
                </Typography>
              </div>
              <div
                data-lesson-dnd-scroll-container
                className="h-[400px] overflow-y-auto"
                data-testid="dnd-sorter-column-scroll-container"
              >
                {getCategories()
                  ?.slice(0, 1)
                  ?.map((category) => (
                    <CategoryDropZone
                      key={category.id}
                      id={category.id}
                      title={category.title}
                      items={category.items}
                      isCorrect={category.isComplete}
                      isIncorrect={false}
                      isLocked={category.isComplete}
                      isDisabled={category.isComplete}
                      onMoveToAvailable={handleMoveToAvailable}
                      itemTexts={itemTexts}
                      getItemCorrectness={getItemCorrectness}
                    />
                  ))}
              </div>
            </div>
          </div>

          {/* Center Column - Current Item */}
          <div className="lg:col-span-1">
            <div className="lg:p-6 p-4 rounded-2xl border border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <GripVertical className="w-5 h-5 text-blue-400" />
                <Typography
                  variant="h6"
                  weight="bold"
                  className="!text-blue-500"
                >
                  Available Items
                </Typography>
              </div>
              <div
                data-lesson-dnd-scroll-container
                className="h-[400px] overflow-y-auto bg-gray-50 p-4 rounded-lg"
                data-testid="dnd-sorter-column-scroll-container"
              >
                {isCorrect ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <Typography variant="p" className="text-gray-500 mb-2">
                        All items are in the correct category.
                      </Typography>
                    </div>
                  </div>
                ) : (
                  <SortableContext
                    items={getDraggableItems()?.map((item) => item?.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {getDraggableItems().map((item, index) => (
                      <DraggableItem
                        key={`${item?.id}-${index}`}
                        id={item?.id}
                        item={item?.text}
                        isMatched={false}
                      />
                    ))}
                  </SortableContext>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Second Category */}
          <div className="lg:col-span-1">
            <div className="bg-white lg:p-6 p-4 rounded-2xl border border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <Typography
                  variant="h6"
                  weight="bold"
                  className="!text-blue-500"
                >
                  🎯 {getCategories()[1]?.title || "Category 2"}
                </Typography>
              </div>
              <div
                data-lesson-dnd-scroll-container
                className="h-[400px] overflow-y-auto"
                data-testid="dnd-sorter-column-scroll-container"
              >
                {getCategories()
                  ?.slice(1, 2)
                  ?.map((category) => (
                    <CategoryDropZone
                      key={category.id}
                      id={category.id}
                      title={category.title}
                      items={category.items}
                      isCorrect={category.isComplete}
                      isIncorrect={false}
                      isLocked={category.isComplete}
                      isDisabled={category.isComplete}
                      onMoveToAvailable={handleMoveToAvailable}
                      itemTexts={itemTexts}
                      getItemCorrectness={getItemCorrectness}
                    />
                  ))}
              </div>
            </div>
          </div>
        </div>

        <DragOverlay>
          {activeId ? (
            <div className="bg-gradient-to-r from-blue-400 to-blue-500 text-white px-6 py-4 rounded-xl shadow-2xl transform rotate-2">
              <div className="flex items-center gap-3">
                <GripVertical className="w-5 h-5 text-blue-200" />
                <Typography variant="p" className="text-white font-medium">
                  {content?.items?.find((item) => item?.id === activeId)?.text}
                </Typography>
              </div>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Action Buttons */}
      <div className="mt-4 flex gap-4 mb-4">
        {!showFeedback && !isCorrect && (
          <Button onClick={handleTryAgain} variant="filled" size="medium">
            Reset Incorrect
          </Button>
        )}
      </div>

      {/* Feedback Message */}
      {showFeedback && (
        <FeedbackMessage
          isSuccess={isCorrect}
          title={isCorrect ? "Perfect!" : "Try again"}
          message={feedbackMessage}
          showTryAgain={!isCorrect}
          onTryAgain={handleTryAgain}
        />
      )}
    </div>
  );
};

export default DragAndDropSorterPreview;
